import type { Dispatch } from 'react';
import { Flex, Text, Button, type StyleProps } from '@chakra-ui/react';
import { _js } from '@ifixit/localize';

/*
 * This is a custom Pagination component implemented with Chakra.
 * Because pagination is a common demand on our site, this was
 * implemented with reusability in mind.
 *
 * This component's responsibilities are limited to:
 *    1. Displaying the current state of pageContext (provided by caller).
 *    2. Calling the setPageContext function (provided by caller)
 *       to update the pageContext upon user interaction.
 *    3. Execute any optional side effects desired when paginating
 *       (provided by caller)
 */

type PaginationProps = {
   pageContext: {
      type: string;
      curPage: number;
      totalPages: number;
      prevPage: boolean;
      nextPage: boolean;
   };
   setPageContext: Dispatch<any>;
   // FIXME: Don't use `Function` as a type. The `Function` type accepts any function-like value.
   // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
   handler?: Function;
   sx?: StyleProps;
};

const Pagination = ({ pageContext, setPageContext, handler, sx }: PaginationProps) => {
   function updatePageContext(diff: number) {
      const newPage = pageContext.curPage + diff;
      const newPrevPage = newPage > 1;
      const newNextPage = newPage < pageContext.totalPages;

      if (newPage > 0 && newPage <= pageContext.totalPages) {
         setPageContext({
            ...pageContext,
            curPage: newPage,
            prevPage: newPrevPage,
            nextPage: newNextPage,
         });
         if (handler) {
            handler();
         }
      }
   }

   return (
      <Flex
         justify="space-between"
         bgColor="gray.100"
         borderWidth={1}
         borderColor="gray.300"
         overflow="hidden"
         {...sx}
      >
         <Flex w="33%" justify="flex-start">
            {pageContext.prevPage ? (
               <Button
                  variant="solid"
                  borderRadius="0"
                  color="gray.600"
                  h="25px"
                  my="auto"
                  fontWeight="semibold"
                  bgColor="transparent"
                  leftIcon={<i className="fa fa-angle-left" aria-hidden="true"></i>}
                  onClick={() => {
                     updatePageContext(-1);
                  }}
               >
                  {_js('Previous')}
               </Button>
            ) : null}
         </Flex>
         <Flex w="33%" justify="center">
            <Text my="auto" fontWeight="semibold">
               {_js('Page %1 of %2', String(pageContext.curPage), String(pageContext.totalPages))}
            </Text>
         </Flex>
         <Flex w="33%" justify="flex-end">
            {pageContext.nextPage ? (
               <Button
                  variant="solid"
                  borderRadius="0"
                  color="gray.600"
                  h="25px"
                  my="auto"
                  fontWeight="semibold"
                  bgColor="transparent"
                  rightIcon={<i className="fa fa-angle-right" aria-hidden="true"></i>}
                  onClick={() => {
                     updatePageContext(1);
                  }}
               >
                  {_js('Next')}
               </Button>
            ) : null}
         </Flex>
      </Flex>
   );
};

export default Pagination;
